import { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { useNavigate } from "react-router-dom";
import { api } from "../api/api";
import { useAuth } from "../context/AuthContext";
import { registerVendorNotification } from "../notifications/notification";
import { setupForegroundNotification } from "../notifications/foregroundNotification";
import { fetchIP } from "../utils/fetchIP";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // UI States
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { login, token } = useAuth();

  useEffect(() => {
    if (token) {
      navigate("/dashboard", { replace: true });
    }
  }, [token, navigate]);

  const handleLogin = async () => {
    // Basic validation to prevent empty submissions
    if (!email || !password) return;

    try {
      setIsLoading(true);
      const frontend_ip = await fetchIP();
      const res = await api.post("/vendor/login", {
        email,
        password,
        frontend_ip
      });

      // ❌ PREVENT WRONG LOGIN
      if (res.data.error) {
        alert(res.data.error);
        setIsLoading(false);
        return;
      }

      // ✅ STORE ID & NEW DUAL TOKENS
      localStorage.setItem("vendor_id", res.data.vendor.id);


      // Pass the updated payload to your Auth Context
      login(res.data);

      // ✅ REGISTER VENDOR NOTIFICATION
      await registerVendorNotification(res.data.vendor.id);

      // ✅ FOREGROUND LISTENER
      setupForegroundNotification();

      navigate("/dashboard", {
        replace: true
      });

    } catch (err) {
      alert("Login failed. Please check your network and try again.");
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        // ✅ Modern vibrant gradient background matching your app's theme
        background: "linear-gradient(135deg, #FF416C 0%, #FF4B2B 100%)",
        p: 2
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 4, sm: 5 },
          width: "100%",
          maxWidth: 420,
          borderRadius: "28px", // Slightly rounder for a modern feel
          boxShadow: "0px 20px 50px rgba(0,0,0,0.15)", // Deeper shadow to contrast the background
          textAlign: "center",
          bgcolor: "rgba(255, 255, 255, 0.98)", // Tiny hint of translucency
          backdropFilter: "blur(10px)"
        }}
      >
        <Typography
          variant="h4"
          fontWeight="900"
          mb={1}
          sx={{
            // ✅ Gradient text effect for the header
            background: "-webkit-linear-gradient(45deg, #FF416C, #FF4B2B)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Vendor Login
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={4} fontWeight="500">
          Welcome back! Please enter your details.
        </Typography>

        <TextField
          fullWidth
          label="Email Address"
          variant="outlined"
          margin="normal"
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          sx={{
            // ✅ Customizing the TextField outlines and focus colors
            "& .MuiOutlinedInput-root": {
              borderRadius: "14px",
              bgcolor: "#f8f9fa",
              "& fieldset": { borderColor: "#e0e0e0" },
              "&:hover fieldset": { borderColor: "#FF4B2B" },
              "&.Mui-focused fieldset": { borderColor: "#FF4B2B", borderWidth: "2px" },
            },
            "& .MuiInputLabel-root.Mui-focused": { color: "#FF4B2B" }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailOutlinedIcon sx={{ color: email ? "#FF4B2B" : "action.active" }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="Password"
          type={showPassword ? "text" : "password"}
          variant="outlined"
          margin="normal"
          onChange={(e) => setPassword(e.target.value)}
          disabled={isLoading}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "14px",
              bgcolor: "#f8f9fa",
              "& fieldset": { borderColor: "#e0e0e0" },
              "&:hover fieldset": { borderColor: "#FF4B2B" },
              "&.Mui-focused fieldset": { borderColor: "#FF4B2B", borderWidth: "2px" },
            },
            "& .MuiInputLabel-root.Mui-focused": { color: "#FF4B2B" }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon sx={{ color: password ? "#FF4B2B" : "action.active" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  disabled={isLoading}
                  sx={{ color: showPassword ? "#FF4B2B" : "action.active" }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <Button
          fullWidth
          variant="contained"
          onClick={handleLogin}
          disabled={isLoading || !email || !password}
          disableElevation
          sx={{
            mt: 4,
            py: 1.8,
            borderRadius: "14px",
            fontWeight: "800",
            fontSize: "1.05rem",
            textTransform: "none",
            background: "linear-gradient(90deg, #FF416C 0%, #FF4B2B 100%)", // ✅ Bright gradient button
            color: "#fff",
            boxShadow: "0px 8px 20px rgba(255, 75, 43, 0.3)",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)", // ✅ Slight lift effect on hover
              boxShadow: "0px 12px 25px rgba(255, 75, 43, 0.4)",
            },
            "&:disabled": {
              background: "#e0e0e0",
              color: "#9e9e9e",
              boxShadow: "none",
            }
          }}
        >
          {isLoading ? <CircularProgress size={26} color="inherit" /> : "Sign In"}
        </Button>
      </Paper>
    </Box>
  );
}
